import React, { useCallback, useState } from "react";
import { defineMessages, FormattedMessage } from "react-intl";
import { Button, useToggleState } from "@yoast/ui-library";
import { PlusSmallIcon } from "@heroicons/react/24/solid";
import { doRequest, prepareInternalRequest } from "shared-frontend/functions/api";
import useRequest from "../../../reactHooks/useRequest";
import { PageHeader } from "../../PageHeader";
import CreatePersonalAccessTokenModal from "./CreatePersonalAccessTokenModal";
import PersonalAccessTokenRevealModal from "./PersonalAccessTokenRevealModal";
import PersonalAccessTokensList from "./PersonalAccessTokensList";
import RevokeConfirmModal from "./RevokeConfirmModal";
import * as styles from "./PersonalAccessTokensPageStyles.module.scss";

const PAT_API_PATH = "personal-access-tokens";

const messages = defineMessages( {
	title: {
		id: "personalAccessTokens.page.title",
		defaultMessage: "Personal access tokens",
	},
	intro: {
		id: "personalAccessTokens.page.intro",
		defaultMessage: "Personal access tokens let you authenticate to the MyYoast API from scripts and CI pipelines without sharing your password. Create a token, copy it once, and use it as a Bearer token.",
	},
	createButton: {
		id: "personalAccessTokens.page.createButton",
		defaultMessage: "New token",
	},
	loadError: {
		id: "personalAccessTokens.page.loadError",
		defaultMessage: "We could not load your personal access tokens. Please refresh the page.",
	},
} );

/**
 * Personal access tokens page: lets a user create, view and revoke their PATs.
 *
 * @returns {JSX.Element} The page.
 */
function PersonalAccessTokensPage() {
	const { data: tokens, isFirstLoad, error, reload } = useRequest( PAT_API_PATH );
	const { data: availableScopes }                  = useRequest( `${ PAT_API_PATH }/available-scopes` );

	const scopesList = Array.isArray( availableScopes ) ? availableScopes : [];

	const [ isCreateOpen, , , openCreate, closeCreate ] = useToggleState( false );
	const [ revokeTarget, setRevokeTarget ]             = useState( null );
	const [ justCreatedToken, setJustCreatedToken ]     = useState( null );
	const [ isRevoking, setRevoking ]                   = useState( false );
	const [ revokeError, setRevokeError ]               = useState( null );

	const handleCreated = useCallback( ( created ) => {
		closeCreate();
		setJustCreatedToken( created );
		reload();
	}, [ closeCreate, reload ] );

	const handleRevokeConfirm = useCallback( async() => {
		if ( ! revokeTarget ) {
			return;
		}
		setRevoking( true );
		setRevokeError( null );
		try {
			const request = prepareInternalRequest( `${ PAT_API_PATH }/${ revokeTarget.id }`, "DELETE" );
			await doRequest( request );
			setRevokeTarget( null );
			reload();
		} catch ( e ) {
			setRevokeError( e.message || String( e ) );
		} finally {
			setRevoking( false );
		}
	}, [ revokeTarget, reload ] );

	const closeRevokeModal = useCallback( () => {
		setRevokeTarget( null );
		setRevokeError( null );
	}, [] );

	const safeTokens = Array.isArray( tokens ) ? tokens : [];

	return (
		<div>
			<PageHeader title={ messages.title } message={ messages.intro } />
			<section className={ styles.section }>
				<div>
					<Button variant="primary" onClick={ openCreate }>
						<PlusSmallIcon className={ styles.actionIcon } />
						<FormattedMessage { ...messages.createButton } />
					</Button>
				</div>
				<PersonalAccessTokensList
					tokens={ safeTokens }
					isLoading={ isFirstLoad }
					error={ error }
					loadErrorMessage={ messages.loadError }
					availableScopes={ scopesList }
					onRevokeClick={ setRevokeTarget }
				/>
			</section>

			<CreatePersonalAccessTokenModal
				isOpen={ isCreateOpen }
				onClose={ closeCreate }
				onCreated={ handleCreated }
				apiPath={ PAT_API_PATH }
				availableScopes={ scopesList }
			/>

			<PersonalAccessTokenRevealModal
				token={ justCreatedToken }
				availableScopes={ scopesList }
				onClose={ () => setJustCreatedToken( null ) }
			/>

			<RevokeConfirmModal
				token={ revokeTarget }
				isRevoking={ isRevoking }
				error={ revokeError }
				onCancel={ closeRevokeModal }
				onConfirm={ handleRevokeConfirm }
			/>
		</div>
	);
}

export default PersonalAccessTokensPage;
